import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = ({ showMenu = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solutionsByIndustry = [
    { id: 'defense', name: 'Defense & Government Operations', path: '/solutions/defense-government' },
    { id: 'call-centers', name: 'Call Centers', path: '/solutions/call-centers' },
    { id: 'staffing', name: 'Staffing & Recruiting', path: '/solutions/staffing-recruiting' },
    { id: 'healthcare', name: 'Healthcare', path: '/solutions/healthcare' },
    { id: 'banks', name: 'Banks & Credit Unions', path: '/solutions/banks-credit-unions' },
    { id: 'education', name: 'Educational Institutions', path: '/solutions/education' },
    { id: 'consultants', name: 'Consultants/Trainers', path: '/solutions/consultants-trainers' },
    { id: 'franchise', name: 'Franchised Organizations', path: '/solutions/franchise' },
    { id: 'logistics', name: 'Logistics & Trucking', path: '/solutions/logistics-trucking' },
    { id: 'technology', name: 'Technology Platforms & Cloud Marketplaces', path: '/solutions/technology-platforms' },
  ];

  const products = [
    { id: 'engine', name: 'Learning Management System', path: '/products/engine' },
    { id: 'cadence', name: 'Role-Play', path: '/products/cadence' },
    { id: 'exchange', name: 'Marketplace', path: '/products/exchange' },
  ];

  if (!showMenu) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/">
              <img
                src="https://customer-assets.emergentagent.com/job_talent-iq-deploy/artifacts/6uqpwydk_Train%20Logo.png"
                alt="Traain Logo"
                className="h-14 w-auto cursor-pointer"
              />
            </a>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/">
              <img
                src="https://customer-assets.emergentagent.com/job_talent-iq-deploy/artifacts/6uqpwydk_Train%20Logo.png"
                alt="Traain Logo"
                className="h-14 w-auto cursor-pointer"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Products Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('products')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100">
                <span>Products</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {openDropdown === 'products' && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    {products.map((product) => (
                      <a
                        key={product.id}
                        href={product.path}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                      >
                        {product.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Solutions By Industry Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('solutions')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100">
                <span>Solutions By Industry</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {openDropdown === 'solutions' && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    {solutionsByIndustry.map((solution) => (
                      <a
                        key={solution.id}
                        href={solution.path}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                      >
                        {solution.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* About */}
            <a
              href="/about"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
            >
              About
            </a>

            {/* Pricing */}
            <a
              href="/pricing"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
            >
              Pricing
            </a>

          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-900" />
            ) : (
              <Menu className="h-6 w-6 text-gray-900" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2">
            {/* Products Mobile */}
            <div className="space-y-1">
              <button
                onClick={() =>
                  setOpenDropdown(openDropdown === 'products-mobile' ? null : 'products-mobile')
                }
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <span>Products</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    openDropdown === 'products-mobile' ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openDropdown === 'products-mobile' && (
                <div className="pl-4 space-y-1">
                  {products.map((product) => (
                    <a
                      key={product.id}
                      href={product.path}
                      className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      {product.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Solutions By Industry Mobile */}
            <div className="space-y-1">
              <button
                onClick={() =>
                  setOpenDropdown(openDropdown === 'solutions-mobile' ? null : 'solutions-mobile')
                }
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <span>Solutions By Industry</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    openDropdown === 'solutions-mobile' ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openDropdown === 'solutions-mobile' && (
                <div className="pl-4 space-y-1">
                  {solutionsByIndustry.map((solution) => (
                    <a
                      key={solution.id}
                      href={solution.path}
                      className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      {solution.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* About Mobile */}
            <a
              href="/about"
              className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              About
            </a>

            {/* Pricing Mobile */}
            <a
              href="/pricing"
              className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Pricing
            </a>

          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;